/**
 * Production-Safe Sonner-API für TakeLead Web
 *
 * - Titel, `description`, Action-Labels, optionales `icon` und **alle** `toast.promise`-Zweige
 *   (loading / success / error / description) gegen Plain Objects abgesichert — kein ToPrimitive-Crash in React.
 * - `toast.custom`: Factory in try/catch mit Fallback-Element.
 * - `dismiss` / `getHistory` / `getToasts` unverändert.
 */
import { createElement, type ReactElement } from "react";
import { toast as sonnerToast, type ExternalToast } from "sonner";
import { looksLikeReactElement, toDisplayString } from "@/lib/utils";

/** UTF-8, in allen Production-Fonts üblich; sichtbarer Ersatz statt leerem Toast. */
const TOAST_TEXT_FALLBACK = "\u2014";

function coerce(message: unknown): string {
  try {
    const raw = toDisplayString(message, TOAST_TEXT_FALLBACK);
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : TOAST_TEXT_FALLBACK;
  } catch {
    return TOAST_TEXT_FALLBACK;
  }
}

function coerceRenderable(r: unknown): string | ReactElement {
  if (r == null) return TOAST_TEXT_FALLBACK;
  if (looksLikeReactElement(r)) return r as ReactElement;
  if (typeof r === "object") {
    if ("then" in (r as object) && typeof (r as { then?: unknown }).then === "function") {
      return TOAST_TEXT_FALLBACK;
    }
    return coerce(r);
  }
  if (typeof r === "string") return r;
  if (typeof r === "number") return Number.isFinite(r) ? String(r) : TOAST_TEXT_FALLBACK;
  return coerce(r);
}

function wrapAsyncRenderable(r: unknown): unknown {
  if (
    r != null &&
    typeof r === "object" &&
    "then" in r &&
    typeof (r as { then?: unknown }).then === "function"
  ) {
    return (r as Promise<unknown>).then(coerceRenderable, () => TOAST_TEXT_FALLBACK);
  }
  return coerceRenderable(r);
}

/** Sonners Promise-Toasts: success/error/description können `(data) => node | Promise<node>` sein. */
function wrapPromiseBranch(v: unknown): unknown {
  if (typeof v === "function") {
    const f = v as (data: unknown) => unknown;
    return (data: unknown) => {
      try {
        return wrapAsyncRenderable(f(data));
      } catch {
        return TOAST_TEXT_FALLBACK;
      }
    };
  }
  return wrapAsyncRenderable(v);
}

function sanitizePromiseLoading(v: unknown): string | ReactElement {
  return coerceRenderable(v);
}

/** `PromiseData<ToastData>` ist generisch — Sanitizer arbeitet strukturell, Cast am Aufruf. */
function sanitizePromiseOptions(data: unknown): unknown {
  if (data == null || typeof data !== "object") return data;
  const d = data as Record<string, unknown>;
  const {
    loading,
    success,
    error,
    description,
    finally: finallyCb,
    ...rest
  } = d;
  const sanitizedRest = sanitizeExternalToast(rest as ExternalToast);
  return {
    ...(sanitizedRest ?? {}),
    ...(loading !== undefined ? { loading: sanitizePromiseLoading(loading) } : {}),
    ...(success !== undefined ? { success: wrapPromiseBranch(success) } : {}),
    ...(error !== undefined ? { error: wrapPromiseBranch(error) } : {}),
    ...(description !== undefined ? { description: wrapPromiseBranch(description) } : {}),
    ...(finallyCb !== undefined ? { finally: finallyCb } : {}),
  };
}

/** Sonner rendert `description` / Action-`label` als React-Kinder — Plain Objects → ToPrimitive-Crash. */
function sanitizeDescriptionLike(
  value: unknown
): ExternalToast["description"] | undefined {
  if (value == null) return undefined;
  if (typeof value === "function") {
    const fn = value as () => unknown;
    return () => {
      try {
        const r = fn();
        if (looksLikeReactElement(r)) return r as ReactElement;
        return coerce(r);
      } catch {
        return TOAST_TEXT_FALLBACK;
      }
    };
  }
  if (looksLikeReactElement(value)) return value as ReactElement;
  if (typeof value === "object") return coerce(value);
  if (typeof value === "string" || typeof value === "number") return value;
  return coerce(value);
}

function isActionShape(v: unknown): v is { label?: unknown; onClick?: unknown } {
  return (
    v != null &&
    typeof v === "object" &&
    !looksLikeReactElement(v) &&
    "onClick" in v &&
    typeof (v as { onClick?: unknown }).onClick === "function"
  );
}

function sanitizeActionLike(
  act: ExternalToast["action"] | ExternalToast["cancel"]
): ExternalToast["action"] | ExternalToast["cancel"] {
  if (act == null || typeof act !== "object" || looksLikeReactElement(act)) return act;
  if (!isActionShape(act)) return act;
  const label = act.label;
  if (
    label != null &&
    typeof label === "object" &&
    !looksLikeReactElement(label)
  ) {
    return { ...act, label: coerce(label) };
  }
  return act;
}

function sanitizeExternalToast(data?: ExternalToast): ExternalToast | undefined {
  if (data == null || typeof data !== "object") return data;
  const out: ExternalToast = { ...data };
  if ("description" in data && data.description != null) {
    out.description = sanitizeDescriptionLike(data.description) as ExternalToast["description"];
  }
  if ("action" in data && data.action != null) {
    out.action = sanitizeActionLike(data.action) as ExternalToast["action"];
  }
  if ("cancel" in data && data.cancel != null) {
    out.cancel = sanitizeActionLike(data.cancel) as ExternalToast["cancel"];
  }
  if ("icon" in data && data.icon != null) {
    const ic = data.icon;
    if (typeof ic === "object" && !looksLikeReactElement(ic)) {
      out.icon = undefined;
    }
  }
  return out;
}

export const toast = Object.assign(
  (message: unknown, data?: ExternalToast) =>
    sonnerToast(coerce(message), sanitizeExternalToast(data)),
  {
    success: (message: unknown, data?: ExternalToast) =>
      sonnerToast.success(coerce(message), sanitizeExternalToast(data)),
    error: (message: unknown, data?: ExternalToast) =>
      sonnerToast.error(coerce(message), sanitizeExternalToast(data)),
    message: (message: unknown, data?: ExternalToast) =>
      sonnerToast.message(coerce(message), sanitizeExternalToast(data)),
    info: (message: unknown, data?: ExternalToast) =>
      sonnerToast.info(coerce(message), sanitizeExternalToast(data)),
    warning: (message: unknown, data?: ExternalToast) =>
      sonnerToast.warning(coerce(message), sanitizeExternalToast(data)),
    loading: (message: unknown, data?: ExternalToast) =>
      sonnerToast.loading(coerce(message), sanitizeExternalToast(data)),
    promise: ((promise, data) =>
      sonnerToast.promise(
        promise,
        sanitizePromiseOptions(data) as NonNullable<Parameters<typeof sonnerToast.promise>[1]>
      )) as typeof sonnerToast.promise,
    custom: ((jsx, data) =>
      sonnerToast.custom(
        (id) => {
          try {
            return jsx(id);
          } catch {
            return createElement("span", { className: "text-sm text-text-muted" }, TOAST_TEXT_FALLBACK);
          }
        },
        sanitizeExternalToast(data)
      )) as typeof sonnerToast.custom,
    dismiss: sonnerToast.dismiss,
    getHistory: sonnerToast.getHistory,
    getToasts: sonnerToast.getToasts,
  }
) as typeof sonnerToast;
